/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Partner_Partner as UpsellPartner } from '__generated__/graphql-types-do-not-use';

import Imgix from 'js/components/Imgix';

import { CardMetadata, ProductCard, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { SectionName } from '@coursera/event-pulse-types';

import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import DefaultImage from 'images/xdp/two-women-standing-and-smiling-at-tablet.jpg';

type Props = {
  index: number;
  productId: string;
  title: string;
  loading: boolean;
  bannerImageUrl?: string | null;
  slug?: string | null;
  productVariant: string;
  partners?: Array<UpsellPartner | null>;
  footerText?: string[];
  tagline?: string | null;
  trackingNamePrefix?: string;
  trackingSectionName?: SectionName;
  trackingIndex?: number;
  productUrl: string;
  isFixedHeight?: boolean;
};

const styles = {
  productCard: () => css`
    height: 100%;
    ${breakpoints.down('xs')} {
      min-height: 200px;

      .cds-ProductCard-base {
        /* Override CDS for cards in small screen size of stackability refresh module not taking full width */
        max-width: unset !important;
      }
    }
  `,
  fixedHeight: css`
    ${breakpoints.up('sm')} {
      height: 460px;
    }
  `,
};

const UpsellCard = ({
  title,
  bannerImageUrl,
  slug,
  productVariant,
  partners,
  footerText,
  tagline,
  trackingNamePrefix,
  productUrl,
  productId,
  index,
  loading,
  trackingSectionName,
  trackingIndex,
  isFixedHeight = true,
}: Props) => {
  const { trackClickProductCard, useGetViewProductCardRef } = useDegreeDescriptionEventingContext();
  const isMobile = useMediaQuery(breakpoints.down('xs'));

  const eventingInfo = { index, productId, productVariant, title, partners, slug, trackingSectionName, trackingIndex };

  const viewRef = useGetViewProductCardRef(eventingInfo);

  const handleOnClick = () => {
    trackClickProductCard(eventingInfo);
  };

  const cardTrackingName = trackingNamePrefix ? `${trackingNamePrefix}_upsell` : 'upsell';

  const renderPreviewImage = () => (
    <Imgix
      aria-hidden="true"
      alt=""
      loading="lazy"
      maxWidth={480}
      maxHeight={270}
      src={bannerImageUrl || DefaultImage}
      imgParams={{
        quality: 40,
      }}
    />
  );

  return (
    <div css={[styles.productCard, isFixedHeight && styles.fixedHeight]} ref={viewRef}>
      <ProductCard
        variant="grid"
        productType={productVariant}
        // @ts-expect-error URL: string
        partners={partners?.map((partner) => ({
          name: partner?.name || '',
          logoUrl: partner?.squareLogo,
        }))}
        title={{
          name: title,
          linkProps: {
            href: productUrl,
            component: TrackedLink2,
            target: '_blank',
            rel: 'noopener noreferrer',
            onClick: handleOnClick,
          },
          customLinkProps: {
            trackingName: cardTrackingName,
            data: {
              slug,
              title,
              partners,
              productVariant,
            },
            withVisibilityTracking: true,
          },
        }}
        body={
          <Typography2 variant="bodySecondary" color="supportText" component="p">
            {tagline}
          </Typography2>
        }
        footer={<CardMetadata metadata={footerText || []} />}
        {...(!isMobile && { renderPreviewImage })}
        loading={loading}
      />
    </div>
  );
};

export default UpsellCard;
